import { useMemo } from "react";
import {
  isCondition,
  validateFilter,
  type FieldDefinition,
  type Filter,
  type FilterNode,
} from "@estelle/domain";

import { GroupEditor } from "@/components/filters/GroupEditor";
import {
  addCondition,
  appendAt,
  removeAt,
  updateAt,
  type Path,
} from "@/components/filters/tree";

/**
 * The tree editor, controlled.
 *
 * The toolbar's own entry point used to live here as `FilterButton` — a
 * trigger button plus this plus an Apply/Clear footer. #349 opens the same
 * tree from two different places (the bar's "advanced" chip and its field
 * dropdown) that need that footer without either owning this component's
 * trigger, so the footer moved to `AdvancedFilterEditor` and this file went
 * back to being just the tree.
 */
export function FilterBuilder({
  value,
  fields,
  onChange,
}: {
  value: Filter;
  fields: FieldDefinition[];
  onChange: (filter: Filter) => void;
}) {
  const problems = useMemo(
    () => validateFilter(value, fields),
    [value, fields],
  );

  return (
    <GroupEditor
      group={value}
      path={[]}
      fields={fields}
      problems={problems}
      onChangeNode={(path: Path, node: FilterNode) => {
        onChange(updateAt(value, path, () => node));
      }}
      onRemove={(path: Path) => {
        onChange(removeAt(value, path));
      }}
      onAppend={(path: Path, node: FilterNode) => {
        // A condition added at the top narrows what is there (`addCondition`):
        // under an OR root a plain append would widen it, which is how the
        // date in #492 came to sit beside the statuses it was meant to
        // narrow. Inside a group it joins that group, whose own AND/OR is
        // the one in front of the person adding to it.
        onChange(
          path.length === 0 && isCondition(node)
            ? addCondition(value, node)
            : appendAt(value, path, node),
        );
      }}
    />
  );
}
